import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User, Database, HelpCircle, Settings as SettingsIcon,
  Camera, MapPin, Phone, Globe, Download, Trash2,
  RefreshCw, ChevronRight, ChevronLeft, Fingerprint, Shield, Volume2,
  MessageCircle, LogOut, Zap, Bell, Egg, Lock,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { VersionStamp } from '@/components/VersionStamp';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSimpleMode } from '@/contexts/SimpleModeContext';
import { Screen } from '@/components/layout/Screen';
import { cn } from '@/lib/utils';
import { offlineStorage } from '@/features/offline';
import { supabase } from '@/integrations/supabase/client';
import { dataSync } from '@/features/network';
import { getLanguagesForCountry } from '@/i18n/config';
import { getConsentTier, type AAKConsentDetails } from '@/features/consent/AAKConsentJourney';

// ─── CBS Poultry Breeds ──────────────────────────────────────
const POULTRY_BREEDS = [
  { id: '1', name: 'Cobb 500', icon: '🐔' },
  { id: '2', name: 'Ross 308', icon: '🐔' },
  { id: '3', name: 'Hubbard Classic', icon: '🐔' },
  { id: '4', name: 'Arbor Acres Plus', icon: '🐔' },
  { id: '5', name: 'Lohmann Meat', icon: '🐔' },
  { id: '6', name: 'Local Desi', icon: '🐓' },
];

// Country code → flag emoji
const COUNTRY_FLAGS: Record<string, string> = {
  BD: '🇧🇩', KH: '🇰🇭', ET: '🇪🇹', KE: '🇰🇪',
  LA: '🇱🇦', PH: '🇵🇭', UG: '🇺🇬', VN: '🇻🇳',
};

// ─── Main Settings Screen ─────────────────────────────────────
export const SettingsScreen = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, logout, updateProfile } = useAuth();
  const { isSimpleMode, toggleSimpleMode } = useSimpleMode();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dynamic language list based on user's country
  const countryCode = user?.countryCode || 'BD';
  const LANGUAGES = useMemo(() => {
    const langs = getLanguagesForCountry(countryCode);
    return langs.map(l => ({
      code: l.code,
      name: l.name,
      nativeName: l.nativeName,
      flag: l.code === 'en' ? '🇬🇧' : (COUNTRY_FLAGS[countryCode] || '🌍'),
    }));
  }, [countryCode]);

  // Profile state — synced from auth
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileLocation, setProfileLocation] = useState(user?.region || user?.village || '');
  const [profileAvatar, setProfileAvatar] = useState(user?.avatar || '');
  const [flockCapacity, setFlockCapacity] = useState(
    localStorage.getItem('agxl_flock_capacity') || '2000'
  );
  const [primaryBreedId, setPrimaryBreedId] = useState(
    localStorage.getItem('agxl_primary_breed') || '1'
  );
  const [isSaving, setIsSaving] = useState(false);

  // GPS state
  const [gpsLat, setGpsLat] = useState<number | null>(null);
  const [gpsLng, setGpsLng] = useState<number | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  // Settings toggles — persisted in localStorage
  const [audioEnabled, setAudioEnabled] = useState(
    () => localStorage.getItem('agxl_audio') !== 'false'
  );
  const [voiceEnabled, setVoiceEnabled] = useState(
    () => localStorage.getItem('agxl_voice') === 'true'
  );
  const [voiceAutoPlay, setVoiceAutoPlay] = useState(
    () => localStorage.getItem('agxl_voice_autoplay') === 'true'
  );
  const [biometricEnabled, setBiometricEnabled] = useState(
    () => localStorage.getItem('agxl_biometric_enrolled') === 'true'
  );
  const [dataSaver, setDataSaver] = useState(
    () => localStorage.getItem('agxl_data_saver') === 'true'
  );
  const [notifications, setNotifications] = useState({
    dailyReminders: localStorage.getItem('agxl_notif_daily') !== 'false',
    scoreUpdates: localStorage.getItem('agxl_notif_score') !== 'false',
  });

  // Dialogs
  const [showClearCache, setShowClearCache] = useState(false);
  const [showDeleteData, setShowDeleteData] = useState(false);
  const [showDisableBiometric, setShowDisableBiometric] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Bottom nav state
  const [activeTab, setActiveTab] = useState('settings');

  // AAK consent state (loaded from profiles.aak_consent_details)
  const [consentDetails, setConsentDetails] = useState<AAKConsentDetails | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    supabase
      .from('profiles')
      .select('aak_consent_details')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!cancelled && data?.aak_consent_details) {
          setConsentDetails(data.aak_consent_details as unknown as AAKConsentDetails);
        }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  const consentTier = getConsentTier(consentDetails);

  // Sync user data on load
  useEffect(() => {
    if (user) {
      setProfileName(user.name || '');
      setProfileLocation(user.region || user.village || '');
      setProfileAvatar(user.avatar || '');
    }
  }, [user]);

  // Persist toggle changes to localStorage
  useEffect(() => { localStorage.setItem('agxl_audio', String(audioEnabled)); }, [audioEnabled]);
  useEffect(() => { localStorage.setItem('agxl_voice', String(voiceEnabled)); }, [voiceEnabled]);
  useEffect(() => { localStorage.setItem('agxl_voice_autoplay', String(voiceAutoPlay)); }, [voiceAutoPlay]);
  useEffect(() => { localStorage.setItem('agxl_data_saver', String(dataSaver)); }, [dataSaver]);
  useEffect(() => { localStorage.setItem('agxl_notif_daily', String(notifications.dailyReminders)); }, [notifications.dailyReminders]);
  useEffect(() => { localStorage.setItem('agxl_notif_score', String(notifications.scoreUpdates)); }, [notifications.scoreUpdates]);
  useEffect(() => { localStorage.setItem('agxl_flock_capacity', flockCapacity); }, [flockCapacity]);
  useEffect(() => { localStorage.setItem('agxl_primary_breed', primaryBreedId); }, [primaryBreedId]);

  // Storage estimate
  const storageUsage = { total: 48.2, photos: 32.1, voiceRecordings: 8.5, cachedData: 7.6 };

  // ─── Handlers ──────────────────────────────────────────────

  const handleCaptureGPS = useCallback(() => {
    if (!navigator.geolocation) {
      toast({ description: t('settings.gpsNotSupported') });
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGpsLat(pos.coords.latitude);
        setGpsLng(pos.coords.longitude);
        setGpsLoading(false);
        toast({ description: `📍 ${t('settings.gpsLocation', { lat: pos.coords.latitude.toFixed(4), lng: pos.coords.longitude.toFixed(4) })}` });
      },
      () => {
        setGpsLoading(false);
        toast({ description: t('settings.gpsFailed') });
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [toast, t]);

  const handleSaveProfile = async () => {
    if (!profileName.trim()) {
      toast({ description: t('settings.nameRequired') });
      return;
    }
    setIsSaving(true);
    try {
      const updates: any = { name: profileName.trim() };
      if (profileLocation.trim()) {
        const parts = profileLocation.split(',').map(p => p.trim());
        if (parts.length > 1) {
          updates.village = parts[0];
          updates.region = parts.slice(1).join(', ');
        } else {
          updates.region = profileLocation.trim();
        }
      }
      if (profileAvatar && !profileAvatar.startsWith('blob:')) {
        updates.avatar = profileAvatar;
      }
      await updateProfile(updates);
      toast({ description: t('settings.profileSaved') });
    } catch {
      toast({ description: t('settings.profileSaveFailed'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({ description: t('settings.imageTooLarge') });
        return;
      }
      const imageUrl = URL.createObjectURL(file);
      setProfileAvatar(imageUrl);
      toast({ description: t('settings.photoUpdated') });
    }
  };

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
    if (user?.id) { supabase.from('profiles').update({ language: code }).eq('id', user.id); }
    if ('vibrate' in navigator) navigator.vibrate(15);
    toast({ description: t('settings.languageChanged', { language: LANGUAGES.find(l => l.code === code)?.nativeName }) });
  };

  const handleClearCache = async () => {
    try {
      await offlineStorage.cleanupOldCache();
      toast({ description: t('settings.cacheCleared') });
    } catch {
      toast({ description: t('settings.cacheClearFailed'), variant: 'destructive' });
    }
    setShowClearCache(false);
  };

  const handleDeleteAllData = async () => {
    try {
      await offlineStorage.clearAllData();
      toast({ description: t('settings.dataDeleted') });
    } catch {
      toast({ description: t('settings.dataDeleteFailed'), variant: 'destructive' });
    }
    setShowDeleteData(false);
  };

  const handleForceSync = async () => {
    try {
      await dataSync.forceFullSync();
      toast({ description: t('settings.syncComplete') });
    } catch {
      toast({ description: t('settings.syncFailed'), variant: 'destructive' });
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth', { replace: true });
    } catch {
      toast({ description: t('settings.logoutFailed'), variant: 'destructive' });
    }
    setShowLogoutConfirm(false);
  };

  const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';

  // ═══════════════════════════════════════════════════════════
  // SIMPLE MODE VIEW — 2×2 Giant Button Grid
  // ═══════════════════════════════════════════════════════════
  if (isSimpleMode) {
    return (
      <Screen showTopBar={false} showBottomNav={true} activeTab={activeTab} onTabChange={setActiveTab}>
        <div className="min-h-screen bg-background p-4 pt-[calc(1rem+env(safe-area-inset-top))]">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => { if ('vibrate' in navigator) navigator.vibrate(10); navigate(-1); }}
              className="w-12 h-12 flex items-center justify-center rounded-full bg-muted active:scale-95"
            >
              <ChevronLeft className="w-7 h-7 text-foreground" />
            </button>
            <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
              <SettingsIcon className="w-7 h-7 text-primary" />
            </div>
            <div className="w-12 h-12" />
          </div>

          {/* 2×2 Grid */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            {/* Language */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const localLang = LANGUAGES.find(l => l.code !== 'en');
                const next = i18n.language === 'en' && localLang ? localLang.code : 'en';
                handleLanguageChange(next);
              }}
              className="h-[140px] rounded-3xl bg-info/10 border-2 border-info/30 flex flex-col items-center justify-center gap-3"
            >
              <span className="text-6xl">{
                (() => {
                  const current = LANGUAGES.find(l => l.code === i18n.language);
                  return current?.flag || '🇬🇧';
                })()
              }</span>
              <Globe className="w-6 h-6 text-info" />
            </motion.button>

            {/* Sound */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setAudioEnabled(!audioEnabled)}
              className={cn(
                "h-[140px] rounded-3xl flex flex-col items-center justify-center gap-3 transition-all",
                audioEnabled
                  ? "bg-success/10 border-2 border-success/30"
                  : "bg-muted border-2 border-muted-foreground/20"
              )}
            >
              <Volume2 className={cn("w-14 h-14", audioEnabled ? "text-success" : "text-muted-foreground")} />
              <span className="text-3xl">{audioEnabled ? '🔊' : '🔇'}</span>
            </motion.button>

            {/* Show Full Settings */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => toggleSimpleMode()}
              className="h-[140px] rounded-3xl bg-primary/10 border-2 border-primary/30 flex flex-col items-center justify-center gap-3"
            >
              <SettingsIcon className="w-14 h-14 text-primary" />
              <span className="text-xs text-muted-foreground">{t('settings.allSettings')}</span>
            </motion.button>

            {/* Help */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => toast({ description: t('settings.helpComingSoon') })}
              className="h-[140px] rounded-3xl bg-warning/10 border-2 border-warning/30 flex flex-col items-center justify-center gap-3"
            >
              <HelpCircle className="w-14 h-14 text-warning" />
              <span className="text-3xl">❓</span>
            </motion.button>
          </div>

          {/* Log Out */}
          <Button
            variant="destructive"
            className="w-full h-16 text-xl gap-2"
            onClick={() => setShowLogoutConfirm(true)}
          >
            <LogOut className="w-6 h-6" />
            {t('settings.logout')}
          </Button>
        </div>

        {/* Logout Dialog */}
        <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('settings.logoutConfirm')}</AlertDialogTitle>
              <AlertDialogDescription>{t('settings.logoutDesc')}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleLogout} className="bg-red-500 hover:bg-red-600">{t('settings.logout')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Screen>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // FULL MODE VIEW — Standard Settings List
  // ═══════════════════════════════════════════════════════════
  return (
    <Screen showTopBar={false} showBottomNav={true} activeTab={activeTab} onTabChange={setActiveTab}>
      <div className="p-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-24 space-y-4 max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-muted active:scale-95"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">{t('settings.title')}</h1>
            <p className="text-xs text-muted-foreground">{t('settings.subtitle')}</p>
          </div>
        </div>

        {/* ── 1. PROFILE ─────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-5 h-5" /> {t('settings.profile')}
            </CardTitle>
            <CardDescription>{t('settings.profileDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Avatar */}
            <div className="flex items-center gap-4">
              <Avatar className="w-16 h-16">
                <AvatarImage src={profileAvatar} />
                <AvatarFallback className="bg-primary/20 text-primary text-lg">
                  {getInitials(profileName || user?.name || 'U')}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <input type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/*" className="hidden" />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" /> {t('settings.changePhoto')}
                </Button>
                <p className="text-xs text-muted-foreground">{t('settings.photoHint')}</p>
              </div>
            </div>

            <Separator />

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="settings-name">{t('settings.name')}</Label>
              <Input
                id="settings-name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
                placeholder={t('settings.namePlaceholder')}
              />
            </div>

            {/* Phone (read-only) */}
            <div className="space-y-2">
              <Label>{t('settings.phone')}</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input className="pl-10 bg-muted" value={user?.phoneNumber || ''} readOnly />
              </div>
            </div>

            {/* Location + GPS */}
            <div className="space-y-2">
              <Label htmlFor="settings-location">{t('settings.location')}</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  id="settings-location"
                  className="pl-10"
                  value={profileLocation}
                  onChange={(e) => setProfileLocation(e.target.value)}
                  placeholder={t('settings.locationPlaceholder')}
                />
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCaptureGPS} disabled={gpsLoading} className="gap-1.5">
                  {gpsLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />}
                  {gpsLoading ? t('settings.gettingGps') : `📍 ${t('settings.useMyGps')}`}
                </Button>
                {gpsLat != null && gpsLng != null && (
                  <span className="text-xs text-muted-foreground">{gpsLat.toFixed(4)}, {gpsLng.toFixed(4)}</span>
                )}
              </div>
            </div>

            {/* Flock Capacity + Primary Breed */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="flockCapacity">{t('settings.flockCapacity')}</Label>
                <Input
                  id="flockCapacity"
                  type="number"
                  value={flockCapacity}
                  onChange={(e) => setFlockCapacity(e.target.value)}
                  placeholder={t('settings.flockCapacityPlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="breed">{t('settings.primaryBreed')}</Label>
                <div className="relative">
                  <Egg className="absolute left-3 top-3 w-4 h-4 text-muted-foreground z-10 pointer-events-none" />
                  <Select value={primaryBreedId} onValueChange={setPrimaryBreedId}>
                    <SelectTrigger id="breed" className="pl-10">
                      <SelectValue placeholder={t('settings.selectBreed')} />
                    </SelectTrigger>
                    <SelectContent>
                      {POULTRY_BREEDS.map((breed) => (
                        <SelectItem key={breed.id} value={breed.id}>
                          {breed.icon} {breed.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveProfile} disabled={isSaving} className="w-full">
              {isSaving ? t('common.saving') : t('settings.saveProfile')}
            </Button>
          </CardContent>
        </Card>

        {/* ── 2. LANGUAGE ────────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="w-5 h-5" /> {t('settings.language')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={cn(
                    "flex-1 py-3 rounded-xl text-center transition-all active:scale-95",
                    i18n.language === lang.code
                      ? "bg-success/20 ring-2 ring-success"
                      : "bg-muted hover:bg-muted/80"
                  )}
                >
                  <span className="text-3xl block">{lang.flag}</span>
                  <span className="text-xs mt-1 block">{lang.nativeName}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ── 5. AUDIO & VOICE ───────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Volume2 className="w-5 h-5" /> {t('settings.audioVoice')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Volume2 className="w-5 h-5 text-primary" />
                <div>
                  <Label className="font-medium">{t('settings.soundEffects')}</Label>
                  <p className="text-xs text-muted-foreground">{t('settings.soundEffectsDesc')}</p>
                </div>
              </div>
              <Switch checked={audioEnabled} onCheckedChange={setAudioEnabled} />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5 text-green-500" />
                <div>
                  <Label className="font-medium">{t('settings.voiceGuide')}</Label>
                  <p className="text-xs text-muted-foreground">{t('settings.voiceGuideDesc')}</p>
                </div>
              </div>
              <Switch checked={voiceEnabled} onCheckedChange={setVoiceEnabled} />
            </div>

            {voiceEnabled && (
              <>
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                  <p className="text-xs text-green-600 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    {t('settings.voiceGuideActive')}
                  </p>
                </div>
                <Separator />
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">{t('settings.autoPlayResponses')}</Label>
                    <p className="text-xs text-muted-foreground">{t('settings.autoPlayResponsesDesc')}</p>
                  </div>
                  <Switch checked={voiceAutoPlay} onCheckedChange={setVoiceAutoPlay} />
                </div>
              </>
            )}
          </CardContent>
        </Card>



        {/* ── DATA & PRIVACY ────────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="w-5 h-5" /> Data & Privacy
            </CardTitle>
            <CardDescription>Control how your photos and data are used</CardDescription>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => navigate('/consent', { state: { mode: 'settings', startScreen: 1 } })}
              className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 active:scale-[0.98] transition-all group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Shield className="w-5 h-5 text-primary" />
                </div>
                <div className="text-left min-w-0">
                  <p className="text-sm font-medium text-foreground">Manage AAK Consent</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {consentTier === 'research'
                      ? 'You contribute to research'
                      : consentTier === 'community'
                        ? 'You help your community'
                        : 'Photos help you only'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {consentTier === 'research' && (
                  <Badge className="bg-info/15 text-info hover:bg-info/15 border-info/30">
                    Research Contributor
                  </Badge>
                )}
                {consentTier === 'community' && (
                  <Badge className="bg-success/15 text-success hover:bg-success/15 border-success/30">
                    Community Contributor
                  </Badge>
                )}
                {consentTier === 'basic' && (
                  <Badge variant="secondary">Basic</Badge>
                )}
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          </CardContent>
        </Card>


        {/* ── 10. ABOUT & HELP ───────────────────────────── */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <HelpCircle className="w-5 h-5" /> {t('settings.aboutHelp')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {[
              { key: 'howToUse', label: t('settings.howToUse') },
              { key: 'faq', label: t('settings.faq') },
              { key: 'contactSupport', label: t('settings.contactSupport') },
            ].map(item => (
              <Button key={item.key} variant="ghost" size="sm" className="w-full justify-between">
                <span className="text-sm">{item.label}</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            ))}
            <Separator className="my-2" />
            {[
              { key: 'privacyPolicy', label: t('settings.privacyPolicy') },
              { key: 'termsOfService', label: t('settings.termsOfService') },
            ].map(item => (
              <Button key={item.key} variant="ghost" size="sm" className="w-full justify-between">
                <span className="text-sm">{item.label}</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            ))}
            <Separator className="my-2" />
            <div className="flex items-center justify-between px-3 py-1">
              <span className="text-xs text-muted-foreground">{t('settings.appVersion')}</span>
              <Badge variant="secondary" className="text-xs">CBS v1.0.0</Badge>
            </div>
          </CardContent>
        </Card>

        {/* ── LOG OUT ────────────────────────────────────── */}
        <Button
          variant="destructive"
          className="w-full h-12 text-base gap-2"
          onClick={() => setShowLogoutConfirm(true)}
        >
          <LogOut className="w-5 h-5" /> {t('settings.logout')}
        </Button>
      </div>

      {/* ── DIALOGS ──────────────────────────────────────── */}
      <AlertDialog open={showClearCache} onOpenChange={setShowClearCache}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.clearCacheConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('settings.clearCacheDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearCache}>{t('common.clear')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteData} onOpenChange={setShowDeleteData}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.deleteAllConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('settings.deleteAllDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAllData} className="bg-red-500 hover:bg-red-600">{t('settings.deleteEverything')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDisableBiometric} onOpenChange={setShowDisableBiometric}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.disableBiometric')}</AlertDialogTitle>
            <AlertDialogDescription>{t('settings.disableBiometricDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              setBiometricEnabled(false);
              localStorage.setItem('agxl_biometric_enrolled', 'false');
              setShowDisableBiometric(false);
              toast({ description: t('settings.biometricDisabled') });
            }}>{t('common.disable')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('settings.logoutConfirm')}</AlertDialogTitle>
            <AlertDialogDescription>{t('settings.logoutDesc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout} className="bg-red-500 hover:bg-red-600">{t('settings.logout')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <VersionStamp className="py-4" />
    </Screen>
  );
};

export default SettingsScreen;
